import {
	FileUpload,
	Wrapper,
	Head,
	Toast,
	Check,
	ErrorCrossIcon,
	HintMessage,
	ContentWrapper,
	renderErrorMessage,
} from '@adac-mp/components';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import {
	IUploadRequest,
	IPicture,
	UploadService,
	UploadState,
	OfferContext,
	DkvContext,
	IDeleteRequest,
	IOfferContext,
	IDkvContext,
	getInitialValue,
	getCountriesByCustomer,
	ICountry,
} from '@adac-mp/shared';
import React, { useContext, useEffect, useState } from 'react';

interface IUploadProps {
	stepCount?: number;
	hasError?: boolean;
	updateValidatedStepsMap?: (hasError: boolean, reset?: boolean) => void;
	updateStepCount?: (stepCount: number) => void;
}

const Upload = ({
	stepCount = 0,
	hasError,
	updateValidatedStepsMap = () => {},
	updateStepCount = () => {},
}: IUploadProps) => {
	const { viewport } = useViewport();
	const { allCountries, offer } = useContext<IOfferContext>(OfferContext);
	const { vehicleRegistration, updateInputFields, updateVehicleRegistration } = useContext<IDkvContext>(DkvContext);
	const [file, setFile] = useState<File | undefined>(undefined);
	const [state, setState] = useState<UploadState>(vehicleRegistration ? 'success' : 'default');
	const [showToast, setShowToast] = useState<boolean>(false);
	const [showErrorToastContext, setShowErrorToastContext] = useState<string | undefined>(undefined);
	const [countries, setCountries] = useState<ICountry[] | []>([]);
	const [deleteButtonDisabled, setDeletButtonDisabled] = useState<boolean>(false);

	const handleUploadedFile = (uploadedFile: string | ArrayBuffer, currentFile: File) => {
		const base64Format =
			typeof uploadedFile === 'string' ? uploadedFile : new TextDecoder().decode(new Uint8Array(uploadedFile));
		const body: IUploadRequest = {
			addOfferHashList: [sessionStorage.getItem('memberOfferHash') || ''],
			offerHash: sessionStorage.getItem('offerHash') || '',
			picture: {
				base64Image: base64Format.split(',')[1],
				name: currentFile.name,
				pictureIssueId: 1,
			},
		};

		UploadService.upload(body).then((res) => {
			if (res.code === 200) {
				const { documentFieldItems, archivePictureId, offerHash, addOfferHashList } = res;
				const updatedFields = documentFieldItems.map((field) => ({
					...field,
					documentValue: getInitialValue(
						field.documentCode,
						field.documentValue,
						countries[0], //first country from the list set as default
						field.dataType,
					),
				}));
				updateInputFields(updatedFields);
				sessionStorage.setItem('offerHash', offerHash);
				sessionStorage.setItem('memberOfferHash', addOfferHashList[0]);
				if (offer) {
					const pictureResponse: IPicture = {
						addOfferHashList,
						offerHash,
						archivePictureId,
						pictureIssueId: 1,
					};
					updateVehicleRegistration(pictureResponse);
					setState('success');
					setShowToast(true);
				}
			} else {
				setState('default');
				setShowErrorToastContext(res.errors[0].errorText);
			}
		});
	};

	// Handling back navigation with browser
	useEffect(() => {
		if (offer) {
			updateStepCount(stepCount + 1);
		}
	}, []);

	useEffect(() => {
		updateValidatedStepsMap(state === 'success');
	}, [state]);

	// On file select (from the pop up)
	const onFileChange = (files: FileList) => {
		setShowErrorToastContext(undefined);
		setShowToast(false);
		const currentFile = files[0];
		setFile(currentFile);

		const allowedFileTypes = ['image/jpeg'];
		if (!allowedFileTypes.includes(currentFile.type)) {
			setShowErrorToastContext('Es kann nur das JPG Format hochgeladen werden. Bitte versuchen Sie es erneut.');
			return;
		}
		if (currentFile.size < 200000 || currentFile.size > 10000000) {
			setShowErrorToastContext(
				'Sie können nur Dateien im JPG-Format hochladen, die mind. 200 KB und max. 10 MB groß sind.',
			);
			return;
		}

		// Create an object of formData
		const reader = new FileReader();
		reader.readAsDataURL(currentFile);
		setState('loading');
		reader.addEventListener('load', (event: ProgressEvent<FileReader>) => {
			if (event.target?.result) {
				handleUploadedFile(event.target.result, currentFile);
			} else {
				setShowErrorToastContext(
					'Ihr Fahrzeugschein konnte nicht hochgeladen werden. Bitte versuchen Sie es erneut.',
				);
				setState('default');
			}
		});
	};

	const onDeleteClick = () => {
		setDeletButtonDisabled(true);
		UploadService.delete(vehicleRegistration as IDeleteRequest).then(() => {
			setFile(undefined);
			setState('default');
			updateVehicleRegistration(undefined);
			updateStepCount(0);
			updateInputFields([]);
			setDeletButtonDisabled(false);
		});
	};

	useEffect(() => {
		if (allCountries && offer?.customerCountries) {
			const countriesByCustomer = getCountriesByCustomer(allCountries, offer.customerCountries);
			setCountries(countriesByCustomer);
		}
	}, [allCountries, offer]);

	return (
		<>
			<Wrapper animateFirstStep>
				<Head
					headline="Zulassungsbescheinigung 1 Ihres Fahrzeugs hochladen"
					description="Bitte laden Sie ein Foto Ihrer Zulassungsbescheinigung Teil 1 hoch (ehemals Fahrzeugschein)"
				/>
				<ContentWrapper>
					{hasError &&
						renderErrorMessage({
							message: `${
								viewport === 'mobile'
									? 'Bitte laden Sie ein Foto hoch.'
									: 'Bitte laden Sie ein Foto Ihres Fahrzeugscheins hoch.'
							}`,
							className: 'mb-5',
						})}
					<FileUpload
						deleteDisabled={deleteButtonDisabled}
						state={state}
						currentFile={file}
						handleChange={onFileChange}
						handleDelete={onDeleteClick}
					/>
					<HintMessage
						className="mt-4"
						hints={[
							{
								tip: '',
								message:
									'Wir benötigen eine Kopie der Zulassungsbescheinigung Teil 1 (Fahrzeugschein), damit die DKV Ihr Fahrzeug bei den Betreibern der nationalen Mautsysteme anmelden kann. Weitere Informationen finden Sie in unserer Datenschutzerklärung.',
							},
						]}
					/>
				</ContentWrapper>
			</Wrapper>
			{showToast && (
				<Toast
					content="Ihr Fahrzeugschein wurde erfolgreich hinzugefügt."
					icon={<Check size="12" />}
					filledIconColor="primary"
					onToastFinished={() => setShowToast(false)}
				/>
			)}
			{showErrorToastContext && (
				<Toast
					content={showErrorToastContext}
					icon={<ErrorCrossIcon size="24" />}
					onToastFinished={() => setShowErrorToastContext(undefined)}
				/>
			)}
		</>
	);
};

export default Upload;
